import React, { useState, useRef, useCallback } from 'react';
import { Trash2, Edit2, Check, X, AlertTriangle } from 'lucide-react';

// Maximum number of bounding boxes allowed (for shelf tests with 20+ products)
const MAX_BOUNDING_BOXES = 30;

export interface BoundingBox {
  id: string;
  label: string;
  x: number;      // Normalized 0-1
  y: number;      // Normalized 0-1
  width: number;  // Normalized 0-1
  height: number; // Normalized 0-1
}

interface BoundingBoxEditorProps {
  imageUrl: string;
  boxes: BoundingBox[];
  onChange: (boxes: BoundingBox[]) => void;
  disabled?: boolean;
  maxBoxes?: number;
}

const BoundingBoxEditor: React.FC<BoundingBoxEditorProps> = ({
  imageUrl,
  boxes,
  onChange,
  disabled = false,
  maxBoxes = MAX_BOUNDING_BOXES
}) => {
  // Check if we've reached the limit
  const isAtLimit = boxes.length >= maxBoxes;
  const isNearLimit = boxes.length >= maxBoxes - 5 && boxes.length < maxBoxes;
  const containerRef = useRef<HTMLDivElement>(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null);
  const [currentBox, setCurrentBox] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [editingBoxId, setEditingBoxId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [newBoxLabel, setNewBoxLabel] = useState('');
  const [showLabelInput, setShowLabelInput] = useState(false);

  // Generate unique ID for new boxes
  const generateId = () => `box_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Handle image load to get dimensions
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImageSize({ width: img.clientWidth, height: img.clientHeight });
  };

  // Convert pixel coordinates to normalized (0-1)
  const pixelToNormalized = useCallback((px: number, py: number) => {
    if (imageSize.width === 0 || imageSize.height === 0) return { x: 0, y: 0 };
    return {
      x: px / imageSize.width,
      y: py / imageSize.height
    };
  }, [imageSize]);

  // Convert normalized to pixel coordinates
  const normalizedToPixel = useCallback((nx: number, ny: number) => {
    return {
      x: nx * imageSize.width,
      y: ny * imageSize.height
    };
  }, [imageSize]);

  // Get mouse position relative to container
  const getMousePosition = (e: React.MouseEvent) => {
    if (!containerRef.current) return { x: 0, y: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  // Start drawing
  const handleMouseDown = (e: React.MouseEvent) => {
    if (disabled || editingBoxId || isAtLimit) return;
    const pos = getMousePosition(e);
    setIsDrawing(true);
    setDrawStart(pos);
    setCurrentBox({ x: pos.x, y: pos.y, width: 0, height: 0 });
  };

  // Continue drawing
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDrawing || !drawStart || disabled) return;
    const pos = getMousePosition(e);
    
    const x = Math.min(drawStart.x, pos.x);
    const y = Math.min(drawStart.y, pos.y);
    const width = Math.abs(pos.x - drawStart.x);
    const height = Math.abs(pos.y - drawStart.y);
    
    setCurrentBox({ x, y, width, height });
  };

  // Finish drawing
  const handleMouseUp = () => {
    if (!isDrawing || !currentBox || disabled) {
      setIsDrawing(false);
      return;
    }
    
    // Minimum size check (at least 20px)
    if (currentBox.width < 20 || currentBox.height < 20) {
      setIsDrawing(false);
      setCurrentBox(null);
      setDrawStart(null);
      return;
    }
    
    // Show label input for new box
    setShowLabelInput(true);
    setIsDrawing(false);
  };

  // Save new box with label
  const handleSaveNewBox = () => {
    if (!currentBox || !newBoxLabel.trim()) return;
    
    const normalized = pixelToNormalized(currentBox.x, currentBox.y);
    const normalizedSize = pixelToNormalized(currentBox.width, currentBox.height);
    
    const newBox: BoundingBox = {
      id: generateId(),
      label: newBoxLabel.trim(),
      x: normalized.x,
      y: normalized.y,
      width: normalizedSize.x,
      height: normalizedSize.y
    };
    
    onChange([...boxes, newBox]);
    setCurrentBox(null);
    setDrawStart(null);
    setNewBoxLabel('');
    setShowLabelInput(false);
  };

  // Cancel new box
  const handleCancelNewBox = () => {
    setCurrentBox(null);
    setDrawStart(null);
    setNewBoxLabel('');
    setShowLabelInput(false);
  };

  // Delete box
  const handleDeleteBox = (id: string) => {
    onChange(boxes.filter(box => box.id !== id));
  };

  // Start editing label
  const handleStartEdit = (box: BoundingBox) => {
    setEditingBoxId(box.id);
    setEditLabel(box.label);
  };

  // Save edited label
  const handleSaveEdit = () => {
    if (!editingBoxId || !editLabel.trim()) return;
    
    onChange(boxes.map(box => 
      box.id === editingBoxId ? { ...box, label: editLabel.trim() } : box
    ));
    setEditingBoxId(null);
    setEditLabel('');
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingBoxId(null);
    setEditLabel('');
  };

  // Handle key press for label inputs
  const handleKeyPress = (e: React.KeyboardEvent, action: 'new' | 'edit') => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action === 'new' ? handleSaveNewBox() : handleSaveEdit();
    } else if (e.key === 'Escape') {
      action === 'new' ? handleCancelNewBox() : handleCancelEdit();
    }
  };

  return (
    <div className="space-y-4">
      {/* Limit warning messages */}
      {isAtLimit && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-700">
            Maximum of {maxBoxes} regions reached. Delete existing regions to add more.
          </p>
        </div>
      )}
      {isNearLimit && (
        <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-xs text-blue-600">
            {maxBoxes - boxes.length} region{maxBoxes - boxes.length !== 1 ? 's' : ''} remaining (max {maxBoxes})
          </p>
        </div>
      )}

      {/* Image with overlay */}
      <div 
        ref={containerRef}
        className={`relative inline-block select-none ${disabled || isAtLimit ? 'cursor-not-allowed' : 'cursor-crosshair'}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => isDrawing && handleMouseUp()}
      >
        <img
          src={imageUrl}
          alt="Hotspot Editor"
          onLoad={handleImageLoad}
          className="max-w-full h-auto rounded-lg border border-gray-200"
          draggable={false}
        />
        
        {/* Existing boxes */}
        {boxes.map((box) => {
          const pos = normalizedToPixel(box.x, box.y);
          const size = normalizedToPixel(box.width, box.height);
          
          return (
            <div
              key={box.id}
              className="absolute border-2 border-teal-500 bg-teal-500/20 pointer-events-none"
              style={{
                left: pos.x,
                top: pos.y,
                width: size.x,
                height: size.y
              }}
            >
              {/* Label badge */}
              <div className="absolute -top-6 left-0 bg-teal-500 text-white text-xs px-2 py-0.5 rounded whitespace-nowrap">
                {box.label}
              </div>
            </div>
          );
        })}
        
        {/* Current drawing box */}
        {currentBox && (
          <div
            className="absolute border-2 border-blue-500 bg-blue-500/20 pointer-events-none"
            style={{
              left: currentBox.x,
              top: currentBox.y,
              width: currentBox.width,
              height: currentBox.height
            }}
          />
        )}
      </div>

      {/* New box label input */}
      {showLabelInput && currentBox && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <span className="text-sm text-gray-700">Label this region:</span>
          <input
            type="text"
            value={newBoxLabel}
            onChange={(e) => setNewBoxLabel(e.target.value)}
            onKeyDown={(e) => handleKeyPress(e, 'new')}
            placeholder="e.g., Product A"
            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            autoFocus
          />
          <button
            onClick={handleSaveNewBox}
            disabled={!newBoxLabel.trim()}
            className="p-1.5 bg-teal-500 text-white rounded hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            onClick={handleCancelNewBox}
            className="p-1.5 bg-gray-400 text-white rounded hover:bg-gray-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Box list */}
      {boxes.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">
            Defined Regions ({boxes.length}/{maxBoxes})
          </h4>
          <div className="space-y-1">
            {boxes.map((box, index) => (
              <div 
                key={box.id}
                className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200"
              >
                {editingBoxId === box.id ? (
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-sm text-gray-500 w-6">{index + 1}.</span>
                    <input
                      type="text"
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      onKeyDown={(e) => handleKeyPress(e, 'edit')}
                      className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-teal-500"
                      autoFocus
                    />
                    <button onClick={handleSaveEdit} className="p-1 text-teal-600 hover:text-teal-700">
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={handleCancelEdit} className="p-1 text-gray-500 hover:text-gray-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <span className="text-sm text-gray-700">
                      <span className="text-gray-500 mr-2">{index + 1}.</span>
                      {box.label}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleStartEdit(box)}
                        disabled={disabled}
                        className="p-1 text-gray-400 hover:text-blue-500 disabled:opacity-50"
                        title="Edit label"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteBox(box.id)}
                        disabled={disabled}
                        className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-50"
                        title="Delete region"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instructions */}
      {!disabled && boxes.length === 0 && !showLabelInput && (
        <p className="text-sm text-gray-500">
          Click and drag on the image to draw product regions. Each region will be labeled for identification.
        </p>
      )}
    </div>
  );
};

export default BoundingBoxEditor;

